/**
 * Validated Integration Form
 * 
 * Production-ready form for creating and managing external API integrations with comprehensive configuration
 */

import {
    Accordion,
    ActionIcon,
    Alert,
    Badge,
    Button,
    Card,
    Checkbox,
    Code,
    Grid,
    Group,
    JsonInput,
    Modal,
    NumberInput,
    Paper,
    PasswordInput,
    Select,
    Stack,
    Tabs,
    Text,
    Textarea,
    TextInput,
    Title
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
    IconAlertTriangle,
    IconApi,
    IconCheck,
    IconEye,
    IconEyeOff,
    IconInfoCircle,
    IconPlug,
    IconRefresh,
    IconShield,
    IconTestPipe,
    IconWorldWww,
    IconX
} from '@tabler/icons-react';
import React, { useState } from 'react';
import { useIntegrationMutations } from '../hooks/useWorkflowState';
import { useValidatedForm, useValidatedFormSubmission } from '../hooks/useValidatedForm';

interface AuthConfig {
    type: 'none' | 'basic' | 'bearer' | 'api_key' | 'oauth2';
    username?: string;
    password?: string;
    token?: string;
    apiKey?: string;
    apiKeyHeader?: string;
    oauth2Config?: {
        clientId: string;
        clientSecret: string;
        authUrl: string;
        tokenUrl: string;
        scopes: string[];
    };
}

interface WebhookConfig {
    enabled: boolean;
    url?: string;
    secret?: string;
    events: string[];
    retryPolicy: {
        maxRetries: number;
        backoffMs: number;
    };
}

interface IntegrationData {
    name: string;
    description?: string;
    type: 'rest_api' | 'graphql' | 'webhook' | 'database' | 'file_system' | 'message_queue';
    baseUrl: string;
    authConfig: AuthConfig;
    headers?: Record<string, string>;
    timeout: number;
    retryPolicy: {
        maxRetries: number;
        backoffMs: number;
        retryableStatusCodes: number[];
    };
    rateLimiting: {
        enabled: boolean;
        requestsPerMinute?: number;
        burstLimit?: number;
    };
    healthCheck: {
        enabled: boolean;
        endpoint?: string;
        intervalMs?: number;
    };
    webhook?: WebhookConfig;
    metadata?: Record<string, any>;
    enabled: boolean;
}

interface ValidatedIntegrationFormProps {
    initialData?: Partial<IntegrationData>;
    mode: 'create' | 'edit';
    onSuccess?: (data: IntegrationData) => void;
    onCancel?: () => void;
}

export const ValidatedIntegrationForm: React.FC<ValidatedIntegrationFormProps> = ({
    initialData,
    mode,
    onSuccess,
    onCancel
}) => {
    const { createIntegration, updateIntegration, testIntegration: testConnection } = useIntegrationMutations();
    const [activeTab, setActiveTab] = useState<string | null>('basic');
    const [testModalOpened, { open: openTestModal, close: closeTestModal }] = useDisclosure(false);
    const [testResult, setTestResult] = useState<any>(null);
    const [testLoading, setTestLoading] = useState(false);
    const [showSecrets, setShowSecrets] = useState(false);

    // Initialize validated form
    const form = useValidatedForm<Partial<IntegrationData>>({
        schemaName: 'Integration' as any,
        initialValues: {
            name: initialData?.name || '',
            description: initialData?.description || '',
            type: initialData?.type || 'rest_api',
            baseUrl: initialData?.baseUrl || '',
            authConfig: initialData?.authConfig || {
                type: 'none'
            },
            headers: initialData?.headers || {},
            timeout: initialData?.timeout || 30000,
            retryPolicy: initialData?.retryPolicy || {
                maxRetries: 3,
                backoffMs: 1000,
                retryableStatusCodes: [500, 502, 503, 504]
            },
            rateLimiting: initialData?.rateLimiting || {
                enabled: false,
                requestsPerMinute: 60,
                burstLimit: 10
            },
            healthCheck: initialData?.healthCheck || {
                enabled: true,
                endpoint: '/health',
                intervalMs: 300000
            },
            webhook: initialData?.webhook || {
                enabled: false,
                events: [],
                retryPolicy: {
                    maxRetries: 3,
                    backoffMs: 1000
                }
            },
            metadata: initialData?.metadata || {},
            enabled: initialData?.enabled !== undefined ? initialData.enabled : true
        },
        validateOnChange: true,
        validateOnBlur: true,
        showNotifications: false
    });

    // Form submission with GraphQL
    const { submitForm, loading: submitting, submissionResult } = useValidatedFormSubmission(
        'Integration' as any,
        () => ({
            mutate: async (data: Partial<IntegrationData>) => {
                if (mode === 'create') {
                    return await createIntegration(data as any);
                } else {
                    return await updateIntegration(initialData?.name || '', data as any);
                }
            },
            loading: false,
            error: null
        })
    );

    // Integration type options
    const typeOptions = [
        { value: 'rest_api', label: 'REST API - Standard HTTP REST services' },
        { value: 'graphql', label: 'GraphQL - GraphQL API endpoint' },
        { value: 'webhook', label: 'Webhook - Receive HTTP callbacks' },
        { value: 'database', label: 'Database - Direct database connection' },
        { value: 'file_system', label: 'File System - File-based integration' },
        { value: 'message_queue', label: 'Message Queue - Queue-based messaging' }
    ];

    // Auth type options
    const authTypeOptions = [
        { value: 'none', label: 'None - No authentication' },
        { value: 'basic', label: 'Basic Auth - Username and password' },
        { value: 'bearer', label: 'Bearer Token - Authorization header token' },
        { value: 'api_key', label: 'API Key - Custom header API key' },
        { value: 'oauth2', label: 'OAuth 2.0 - OAuth 2.0 flow' }
    ];

    // Webhook event options
    const webhookEventOptions = [
        { value: 'workflow.started', label: 'Workflow Started' },
        { value: 'workflow.completed', label: 'Workflow Completed' },
        { value: 'workflow.failed', label: 'Workflow Failed' },
        { value: 'task.assigned', label: 'Task Assigned' },
        { value: 'task.completed', label: 'Task Completed' },
        { value: 'policy.violated', label: 'Policy Violated' },
        { value: 'system.error', label: 'System Error' }
    ];

    // Timeout presets (in milliseconds)
    const timeoutPresets = [
        { value: 5000, label: '5 seconds' },
        { value: 10000, label: '10 seconds' },
        { value: 30000, label: '30 seconds' },
        { value: 60000, label: '1 minute' },
        { value: 120000, label: '2 minutes' },
        { value: 300000, label: '5 minutes' }
    ];

    // Test connection
    const handleTestConnection = async () => {
        setTestLoading(true);
        try {
            const result = await testConnection(form.values as any);
            setTestResult(result);

            notifications.show({
                title: result.success ? 'Connection Successful' : 'Connection Failed',
                message: result.message || (result.success ? 'Connection test passed' : 'Connection test failed'),
                color: result.success ? 'green' : 'red'
            });
        } catch (error) {
            notifications.show({
                title: 'Test Failed',
                message: (error as Error).message,
                color: 'red'
            });
            setTestResult({ success: false, message: (error as Error).message });
        } finally {
            setTestLoading(false);
        }
    };

    // Handle form submission
    const handleSubmit = async (values: Partial<IntegrationData>) => {
        const result = await submitForm(values);
        if (result.success && onSuccess) {
            onSuccess(result.data);
        }
    };

    const getTypeColor = (type: string) => {
        switch (type) {
            case 'rest_api': return 'blue';
            case 'graphql': return 'purple';
            case 'webhook': return 'green';
            case 'database': return 'orange';
            case 'file_system': return 'yellow';
            case 'message_queue': return 'red';
            default: return 'gray';
        }
    };

    return (
        <form onSubmit={form.onSubmit((values) => handleSubmit(values as Partial<IntegrationData>))}>
            <Stack gap="lg">
                {/* Header */}
                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Group>
                                <Title order={3}>
                                    {mode === 'create' ? 'Create' : 'Edit'} Integration
                                </Title>
                                <IconPlug size={24} color="blue" />
                            </Group>
                            <Text size="sm" c="dimmed">
                                Configure external API and service integrations with authentication and monitoring
                            </Text>
                        </div>
                        <Group>
                            <Badge
                                color={form.validationResult.valid ? 'green' : 'red'}
                                leftSection={form.validationResult.valid ? <IconCheck size={14} /> : <IconX size={14} />}
                            >
                                {form.validationResult.valid ? 'Valid' : `${form.validationResult.errors.length} Error${form.validationResult.errors.length > 1 ? 's' : ''}`}
                            </Badge>
                            <Button
                                variant="light"
                                leftSection={<IconTestPipe size={16} />}
                                onClick={openTestModal}
                                disabled={!form.validationResult.valid}
                            >
                                Test Connection
                            </Button>
                        </Group>
                    </Group>
                </Paper>

                {/* Validation Summary */}
                {form.validationResult.errors.length > 0 && (
                    <Alert color="red" title="Validation Errors" icon={<IconAlertTriangle />}>
                        <Stack gap="xs">
                            {form.validationResult.errors.map((error, index) => (
                                <Text key={index} size="sm">
                                    <strong>{error.field}:</strong> {error.message}
                                </Text>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {form.validationResult.warnings && form.validationResult.warnings.length > 0 && (
                    <Alert color="yellow" title="Validation Warnings" icon={<IconInfoCircle />}>
                        <Stack gap="xs">
                            {form.validationResult.warnings.map((warning, index) => (
                                <Text key={index} size="sm">
                                    <strong>{warning.field}:</strong> {warning.message}
                                    {warning.suggestion && <Text c="dimmed"> • {warning.suggestion}</Text>}
                                </Text>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {/* Integration Form Tabs */}
                <Tabs value={activeTab} onChange={(v) => setActiveTab(v ?? '')}>
                    <Tabs.List>
                        <Tabs.Tab value="basic" leftSection={<IconInfoCircle size={16} />}>
                            Basic Info
                        </Tabs.Tab>
                        <Tabs.Tab value="auth" leftSection={<IconShield size={16} />}>
                            Authentication
                        </Tabs.Tab>
                        <Tabs.Tab value="config" leftSection={<IconApi size={16} />}>
                            Configuration
                        </Tabs.Tab>
                        <Tabs.Tab value="webhooks" leftSection={<IconWorldWww size={16} />}>
                            Webhooks
                        </Tabs.Tab>
                    </Tabs.List>

                    {/* Basic Information Tab */}
                    <Tabs.Panel value="basic">
                        <Card withBorder mt="md">
                            <Stack gap="md">
                                <TextInput
                                    label="Integration Name"
                                    placeholder="Salesforce API Integration"
                                    required
                                    {...form.getInputProps('name')}
                                    error={form.getFieldError('name')}
                                    rightSection={
                                        form.isFieldValid('name') && form.values.name ?
                                            <IconCheck size={16} color="green" /> : undefined
                                    }
                                />

                                <Textarea
                                    label="Description"
                                    placeholder="Integration with Salesforce CRM for customer data synchronization..."
                                    minRows={3}
                                    {...form.getInputProps('description')}
                                    error={form.getFieldError('description')}
                                />

                                <Group grow>
                                    <Select
                                        label="Integration Type"
                                        data={typeOptions}
                                        required
                                        {...form.getInputProps('type')}
                                        error={form.getFieldError('type')}
                                        rightSection={
                                            <Badge size="xs" color={getTypeColor(form.values.type || '')}>
                                                {form.values.type}
                                            </Badge>
                                        }
                                    />

                                    <Checkbox
                                        label="Integration Enabled"
                                        description="Enable this integration for use"
                                        {...form.getInputProps('enabled', { type: 'checkbox' })}
                                    />
                                </Group>

                                <TextInput
                                    label="Base URL"
                                    placeholder="https://api.example.com/v1"
                                    required
                                    {...form.getInputProps('baseUrl')}
                                    error={form.getFieldError('baseUrl')}
                                    leftSection={<IconWorldWww size={16} />}
                                />
                            </Stack>
                        </Card>
                    </Tabs.Panel>

                    {/* Authentication Tab */}
                    <Tabs.Panel value="auth">
                        <Card withBorder mt="md">
                            <Stack gap="md">
                                <Group justify="space-between">
                                    <Title order={4}>Authentication Configuration</Title>
                                    <ActionIcon
                                        variant="subtle"
                                        onClick={() => setShowSecrets(!showSecrets)}
                                    >
                                        {showSecrets ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                                    </ActionIcon>
                                </Group>

                                <Select
                                    label="Authentication Type"
                                    data={authTypeOptions}
                                    value={form.values.authConfig?.type}
                                    onChange={(value: string | null) => form.setFieldValue('authConfig.type', value as any)}
                                    error={form.getFieldError('authConfig.type')}
                                />

                                {form.values.authConfig?.type === 'basic' && (
                                    <Group grow>
                                        <TextInput
                                            label="Username"
                                            placeholder="api_user"
                                            value={form.values.authConfig.username || ''}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('authConfig.username', e.currentTarget.value)}
                                        />
                                        <PasswordInput
                                            label="Password"
                                            placeholder="Enter password"
                                            visible={showSecrets}
                                            value={form.values.authConfig.password || ''}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('authConfig.password', e.currentTarget.value)}
                                        />
                                    </Group>
                                )}

                                {form.values.authConfig?.type === 'bearer' && (
                                    <PasswordInput
                                        label="Bearer Token"
                                        placeholder="Enter bearer token"
                                        visible={showSecrets}
                                        value={form.values.authConfig.token || ''}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('authConfig.token', e.currentTarget.value)}
                                    />
                                )}

                                {form.values.authConfig?.type === 'api_key' && (
                                    <Group grow>
                                        <TextInput
                                            label="API Key Header"
                                            placeholder="X-API-Key"
                                            value={form.values.authConfig.apiKeyHeader || ''}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('authConfig.apiKeyHeader', e.currentTarget.value)}
                                        />
                                        <PasswordInput
                                            label="API Key"
                                            placeholder="Enter API key"
                                            visible={showSecrets}
                                            value={form.values.authConfig.apiKey || ''}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('authConfig.apiKey', e.currentTarget.value)}
                                        />
                                    </Group>
                                )}

                                {form.values.authConfig?.type === 'oauth2' && (
                                    <Accordion>
                                        <Accordion.Item value="oauth2">
                                            <Accordion.Control>OAuth 2.0 Configuration</Accordion.Control>
                                            <Accordion.Panel>
                                                <Stack gap="md">
                                                    <Group grow>
                                                        <TextInput
                                                            label="Client ID"
                                                            value={form.values.authConfig.oauth2Config?.clientId || ''}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('authConfig.oauth2Config.clientId', e.currentTarget.value)}
                                                        />
                                                        <PasswordInput
                                                            label="Client Secret"
                                                            visible={showSecrets}
                                                            value={form.values.authConfig.oauth2Config?.clientSecret || ''}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('authConfig.oauth2Config.clientSecret', e.currentTarget.value)}
                                                        />
                                                    </Group>
                                                    <Group grow>
                                                        <TextInput
                                                            label="Authorization URL"
                                                            value={form.values.authConfig.oauth2Config?.authUrl || ''}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('authConfig.oauth2Config.authUrl', e.currentTarget.value)}
                                                        />
                                                        <TextInput
                                                            label="Token URL"
                                                            value={form.values.authConfig.oauth2Config?.tokenUrl || ''}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('authConfig.oauth2Config.tokenUrl', e.currentTarget.value)}
                                                        />
                                                    </Group>
                                                    <TextInput
                                                        label="Scopes (comma-separated)"
                                                        placeholder="read,write,admin"
                                                        value={form.values.authConfig.oauth2Config?.scopes?.join(',') || ''}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('authConfig.oauth2Config.scopes', e.currentTarget.value.split(',').map(s => s.trim()).filter(Boolean))}
                                                    />
                                                </Stack>
                                            </Accordion.Panel>
                                        </Accordion.Item>
                                    </Accordion>
                                )}

                                <JsonInput
                                    label="Custom Headers (JSON)"
                                    placeholder='{ "User-Agent": "MyApp/1.0", "Accept": "application/json" }'
                                    validationError="Invalid JSON"
                                    formatOnBlur
                                    autosize
                                    minRows={3}
                                    maxRows={6}
                                    value={typeof form.values.headers === 'string' ? form.values.headers : JSON.stringify(form.values.headers || {}, null, 2)}
                                    onChange={(value) => {
                                        try {
                                            const parsed = JSON.parse(value || '{}');
                                            form.setFieldValue('headers', parsed);
                                        } catch {
                                            form.setFieldValue('headers', value);
                                        }
                                    }}
                                    error={form.getFieldError('headers')}
                                />
                            </Stack>
                        </Card>
                    </Tabs.Panel>

                    {/* Configuration Tab */}
                    <Tabs.Panel value="config">
                        <Card withBorder mt="md">
                            <Stack gap="md">
                                <Title order={4}>Request Configuration</Title>

                                <Select
                                    label="Request Timeout"
                                    data={timeoutPresets.map(preset => ({
                                        value: preset.value.toString(),
                                        label: preset.label
                                    }))}
                                    value={form.values.timeout?.toString()}
                                    onChange={(value: string | null) => form.setFieldValue('timeout', value ? parseInt(value) : 30000)}
                                    error={form.getFieldError('timeout')}
                                />

                                <Title order={5} mt="md">Retry Policy</Title>
                                <Grid>
                                    <Grid.Col span={6}>
                                        <NumberInput
                                            label="Max Retries"
                                            min={0}
                                            max={10}
                                            value={form.values.retryPolicy?.maxRetries}
                                            onChange={(value: string | number | undefined) => form.setFieldValue('retryPolicy.maxRetries', value)}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={6}>
                                        <NumberInput
                                            label="Backoff (ms)"
                                            min={100}
                                            step={100}
                                            value={form.values.retryPolicy?.backoffMs}
                                            onChange={(value: string | number | undefined) => form.setFieldValue('retryPolicy.backoffMs', value)}
                                        />
                                    </Grid.Col>
                                </Grid>

                                <TextInput
                                    label="Retryable Status Codes (comma-separated)"
                                    placeholder="500,502,503,504"
                                    value={form.values.retryPolicy?.retryableStatusCodes?.join(',') || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('retryPolicy.retryableStatusCodes', e.currentTarget.value.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n)))}
                                />

                                <Title order={5} mt="md">Rate Limiting</Title>
                                <Checkbox
                                    label="Enable Rate Limiting"
                                    checked={form.values.rateLimiting?.enabled || false}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('rateLimiting.enabled', e.currentTarget.checked)}
                                />

                                {form.values.rateLimiting?.enabled && (
                                    <Grid>
                                        <Grid.Col span={6}>
                                            <NumberInput
                                                label="Requests Per Minute"
                                                min={1}
                                                value={form.values.rateLimiting.requestsPerMinute}
                                                onChange={(value: string | number | undefined) => form.setFieldValue('rateLimiting.requestsPerMinute', value)}
                                            />
                                        </Grid.Col>
                                        <Grid.Col span={6}>
                                            <NumberInput
                                                label="Burst Limit"
                                                min={1}
                                                value={form.values.rateLimiting.burstLimit}
                                                onChange={(value: string | number | undefined) => form.setFieldValue('rateLimiting.burstLimit', value)}
                                            />
                                        </Grid.Col>
                                    </Grid>
                                )}

                                <Title order={5} mt="md">Health Check</Title>
                                <Checkbox
                                    label="Enable Health Check"
                                    checked={form.values.healthCheck?.enabled || false}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('healthCheck.enabled', e.currentTarget.checked)}
                                />

                                {form.values.healthCheck?.enabled && (
                                    <Grid>
                                        <Grid.Col span={6}>
                                            <TextInput
                                                label="Health Check Endpoint"
                                                placeholder="/health"
                                                value={form.values.healthCheck.endpoint || ''}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('healthCheck.endpoint', e.currentTarget.value)}
                                            />
                                        </Grid.Col>
                                        <Grid.Col span={6}>
                                            <NumberInput
                                                label="Check Interval (ms)"
                                                min={30000}
                                                step={30000}
                                                value={form.values.healthCheck.intervalMs}
                                                onChange={(value: string | number | undefined) => form.setFieldValue('healthCheck.intervalMs', value)}
                                            />
                                        </Grid.Col>
                                    </Grid>
                                )}
                            </Stack>
                        </Card>
                    </Tabs.Panel>

                    {/* Webhooks Tab */}
                    <Tabs.Panel value="webhooks">
                        <Card withBorder mt="md">
                            <Stack gap="md">
                                <Group justify="space-between">
                                    <Title order={4}>Webhook Configuration</Title>
                                    <Checkbox
                                        label="Enable Webhooks"
                                        checked={form.values.webhook?.enabled || false}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('webhook.enabled', e.currentTarget.checked)}
                                    />
                                </Group>

                                {form.values.webhook?.enabled && (
                                    <>
                                        <Group grow>
                                            <TextInput
                                                label="Webhook URL"
                                                placeholder="https://your-app.com/webhooks"
                                                value={form.values.webhook.url || ''}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('webhook.url', e.currentTarget.value)}
                                            />
                                            <PasswordInput
                                                label="Webhook Secret"
                                                placeholder="Secret for webhook verification"
                                                visible={showSecrets}
                                                value={form.values.webhook.secret || ''}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('webhook.secret', e.currentTarget.value)}
                                            />
                                        </Group>

                                        <Text size="sm" fw={500}>Events to Send</Text>
                                        <Stack gap="xs">
                                            {webhookEventOptions.map(event => (
                                                <Checkbox
                                                    key={event.value}
                                                    label={event.label}
                                                    checked={form.values.webhook?.events?.includes(event.value) || false}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        const currentEvents = form.values.webhook?.events || [];
                                                        if (e.currentTarget.checked) {
                                                            form.setFieldValue('webhook.events', [...currentEvents, event.value]);
                                                        } else {
                                                            form.setFieldValue('webhook.events', currentEvents.filter((ev: string) => ev !== event.value));
                                                        }
                                                    }}
                                                />
                                            ))}
                                        </Stack>

                                        <Title order={5} mt="md">Webhook Retry Policy</Title>
                                        <Grid>
                                            <Grid.Col span={6}>
                                                <NumberInput
                                                    label="Max Retries"
                                                    min={0}
                                                    max={10}
                                                    value={form.values.webhook.retryPolicy?.maxRetries}
                                                    onChange={(value: string | number | undefined) => form.setFieldValue('webhook.retryPolicy.maxRetries', value)}
                                                />
                                            </Grid.Col>
                                            <Grid.Col span={6}>
                                                <NumberInput
                                                    label="Backoff (ms)"
                                                    min={100}
                                                    step={100}
                                                    value={form.values.webhook.retryPolicy?.backoffMs}
                                                    onChange={(value: string | number | undefined) => form.setFieldValue('webhook.retryPolicy.backoffMs', value)}
                                                />
                                            </Grid.Col>
                                        </Grid>
                                    </>
                                )}
                            </Stack>
                        </Card>
                    </Tabs.Panel>
                </Tabs>

                {/* Form Actions */}
                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Text size="sm" c="dimmed">
                                Integration will be validated before submission
                            </Text>
                            {form.isValidating && (
                                <Text size="sm" c="blue">
                                    Validating...
                                </Text>
                            )}
                        </div>

                        <Group>
                            <Button variant="light" onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                loading={submitting || form.isValidating}
                                disabled={!form.validationResult.valid}
                            >
                                {mode === 'create' ? 'Create Integration' : 'Update Integration'}
                            </Button>
                        </Group>
                    </Group>
                </Paper>

                {/* Submission Result */}
                {submissionResult && (
                    <Alert
                        color={submissionResult.success ? 'green' : 'red'}
                        title={submissionResult.success ? 'Success' : 'Error'}
                    >
                        {submissionResult.success ?
                            'Integration saved successfully!' :
                            submissionResult.error
                        }
                    </Alert>
                )}

                {/* Test Connection Modal */}
                <Modal opened={testModalOpened} onClose={closeTestModal} title="Test Connection" size="md">
                    <Stack gap="md">
                        <Text size="sm" c="dimmed">
                            Test the connection to verify your integration configuration
                        </Text>

                        {testResult && (
                            <Alert
                                color={testResult.success ? 'green' : 'red'}
                                title={testResult.success ? 'Connection Successful' : 'Connection Failed'}
                                icon={testResult.success ? <IconCheck /> : <IconX />}
                            >
                                <Stack gap="sm">
                                    <Text>{testResult.message}</Text>
                                    {testResult.details && (
                                        <Code block>
                                            {JSON.stringify(testResult.details, null, 2)}
                                        </Code>
                                    )}
                                </Stack>
                            </Alert>
                        )}

                        <Group justify="flex-end">
                            <Button variant="light" onClick={closeTestModal}>
                                Close
                            </Button>
                            <Button
                                leftSection={<IconRefresh size={16} />}
                                onClick={handleTestConnection}
                                loading={testLoading}
                            >
                                Test Connection
                            </Button>
                        </Group>
                    </Stack>
                </Modal>
            </Stack>
        </form>
    );
};

export default ValidatedIntegrationForm;